import { useState } from 'react'
import { ROLE } from '@ovrsee/shared'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Button, Input, Select, Text, TextArea } from '../../ui/index.js'
import { FormError } from '../auth/FormError.js'
import {
  STATUS_LABEL,
  useCreatePrompt,
  useCurrentPrompt,
  usePrompts,
  useReviewSubmission,
  useSubmissions,
  useSubmitIdea,
  type SubmissionItem,
} from './useInnovation.js'

function StatusPill({ status }: { status: SubmissionItem['status'] }) {
  const tone =
    status === 'ADOPTED'
      ? { bg: 'var(--positive-bg)', fg: 'var(--positive)' }
      : status === 'NOT_PURSUED'
        ? { bg: 'var(--bg-subtle)', fg: 'var(--fg-muted)' }
        : { bg: 'var(--brand-soft)', fg: 'var(--mauve-700)' }
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        background: tone.bg,
        color: tone.fg,
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  )
}

function CurrentPromptCard({ question }: { question: string }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--o-space-5)',
        marginBottom: 'var(--o-space-5)',
      }}
    >
      <div className="card-label">This period&rsquo;s challenge</div>
      <h2 style={{ margin: '4px 0 0', fontSize: 20, color: 'var(--fg)' }}>{question}</h2>
    </div>
  )
}

function EmployeeInnovation() {
  const current = useCurrentPrompt()
  const submissions = useSubmissions()
  const submitIdea = useSubmitIdea()
  const [body, setBody] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const prompt = current.data?.prompt ?? null

  async function submit() {
    if (prompt === null || body.trim() === '') return
    setFormError(null)
    try {
      await submitIdea.mutateAsync({ promptId: prompt.id, body: body.trim() })
      setBody('')
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not send that idea.')
    }
  }

  return (
    <div className="page">
      {current.isPending ? <LoadingState label="Loading the challenge" /> : null}
      {current.isError ? <ErrorState error={current.error} onRetry={() => void current.refetch()} /> : null}

      {current.isSuccess && prompt === null ? (
        <EmptyState
          title="No challenge is open right now"
          hint="Your manager will post the next one. Past ideas stay below."
        />
      ) : null}

      {prompt !== null ? (
        <>
          <CurrentPromptCard question={prompt.question} />
          <div className="page-section" style={{ marginBottom: 'var(--o-space-5)' }}>
            <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>
              Share an idea
            </div>
            <FormError message={formError} />
            <label className="field-label" htmlFor="idea-body">
              Your idea
            </label>
            <TextArea
              id="idea-body"
              aria-label="Your idea"
              rows={4}
              value={body}
              onChange={setBody}
            />
            <div className="row-actions" style={{ marginTop: 'var(--o-space-3)' }}>
              <Button
                type="button"
                kind="primary"
                size="small"
                disabled={body.trim() === '' || submitIdea.isPending}
                onClick={() => void submit()}
              >
                Send idea
              </Button>
            </div>
            <Text color="secondary">You can send more than one idea for a challenge.</Text>
          </div>
        </>
      ) : null}

      <div className="page-section">
        <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>
          Your ideas
        </div>
        {submissions.isPending ? <LoadingState label="Loading your ideas" /> : null}
        {submissions.isSuccess && submissions.data.length === 0 ? (
          <EmptyState title="You have not sent an idea yet." />
        ) : null}
        {(submissions.data ?? []).map((item) => (
          <article
            key={item.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              padding: 'var(--o-space-4)',
              marginBottom: 'var(--o-space-3)',
            }}
          >
            <div className="row-between" style={{ gap: 'var(--o-space-3)', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--fg-muted)', fontSize: 12.5, minWidth: 0 }}>
                {item.prompt?.question ?? 'Challenge'}
              </span>
              <StatusPill status={item.status} />
            </div>
            <div style={{ color: 'var(--fg)', marginTop: 6 }}>{item.body}</div>
          </article>
        ))}
      </div>
    </div>
  )
}

export function Innovation() {
  usePageTitle('Innovation')
  const { user } = useAuth()
  const isManagerial = user !== null && user.role !== ROLE.EMPLOYEE

  return (
    <>
      <Topbar title="Innovation" eyebrow={isManagerial ? 'Challenges and ideas' : 'Share an idea'} />
      {isManagerial ? <ManagerInnovation /> : <EmployeeInnovation />}
    </>
  )
}

/** Default window length in days for the cadence the manager picks (spec §2.2). */
const CADENCE_DAYS: Record<string, number> = { WEEKLY: 7, MONTHLY: 30, QUARTERLY: 90 }

function ManagerInnovation() {
  const prompts = usePrompts(true)
  const submissions = useSubmissions()
  const createPrompt = useCreatePrompt()
  const review = useReviewSubmission()

  const [question, setQuestion] = useState('')
  const [cadence, setCadence] = useState('WEEKLY')
  const [formError, setFormError] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<string, string>>({})

  async function postPrompt() {
    if (question.trim() === '') return
    setFormError(null)
    // Derived from the clock, never a literal: opens now, closes a cadence later.
    const opensAt = new Date()
    const closesAt = new Date(opensAt.getTime() + (CADENCE_DAYS[cadence] ?? 7) * 86_400_000)
    try {
      await createPrompt.mutateAsync({
        question: question.trim(),
        opensAt: opensAt.toISOString(),
        closesAt: closesAt.toISOString(),
      })
      setQuestion('')
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not post that challenge.')
    }
  }

  return (
    <div className="page">
      <div className="page-section" style={{ marginBottom: 'var(--o-space-5)' }}>
        <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>
          Post a challenge
        </div>
        <FormError message={formError} />
        <label className="field-label" htmlFor="prompt-question">
          Challenge question
        </label>
        <Input
          id="prompt-question"
          value={question}
          onChange={setQuestion}
          aria-label="Challenge question"
        />
        <div className="row-actions" style={{ marginTop: 'var(--o-space-3)', flexWrap: 'wrap' }}>
          <Select
            aria-label="Runs for"
            value={cadence}
            onChange={setCadence}
            style={{
              padding: '7px 10px',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--fg)',
              fontSize: 13,
              minWidth: 0,
            }}
          >
            <option value="WEEKLY">Runs for a week</option>
            <option value="MONTHLY">Runs for a month</option>
            <option value="QUARTERLY">Runs for a quarter</option>
          </Select>
          <Button
            type="button"
            kind="primary"
            size="small"
            disabled={question.trim() === '' || createPrompt.isPending}
            onClick={() => void postPrompt()}
          >
            Post challenge
          </Button>
        </div>
      </div>

      <div className="page-section" style={{ marginBottom: 'var(--o-space-5)' }}>
        <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>
          Challenges
        </div>
        {prompts.isSuccess && prompts.data.length === 0 ? (
          <EmptyState title="No challenges yet" hint="Post one above and ideas will land here." />
        ) : null}
        {(prompts.data ?? []).map((item) => (
          <div key={item.id} style={{ color: 'var(--fg-2)', fontSize: 13, padding: '4px 0' }}>
            {item.question}
          </div>
        ))}
      </div>

      <div className="page-section">
        <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>
          Ideas from your team
        </div>
        {submissions.isPending ? <LoadingState label="Loading ideas" /> : null}
        {submissions.isError ? <ErrorState error={submissions.error} onRetry={() => void submissions.refetch()} /> : null}
        {submissions.isSuccess && submissions.data.length === 0 ? (
          <EmptyState title="No ideas yet" hint="They appear here as your team sends them." />
        ) : null}

        {(submissions.data ?? []).map((item) => (
          <article
            key={item.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              padding: 'var(--o-space-4)',
              marginBottom: 'var(--o-space-3)',
            }}
          >
            <div className="row-between" style={{ gap: 'var(--o-space-3)', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--fg)', fontWeight: 500, minWidth: 0 }}>
                {item.author?.name ?? 'Team member'}
              </span>
              <StatusPill status={item.status} />
            </div>
            <div style={{ color: 'var(--fg-2)', fontSize: 12.5 }}>{item.prompt?.question}</div>
            <div style={{ color: 'var(--fg)', marginTop: 6 }}>{item.body}</div>

            <label className="field-label" style={{ marginTop: 'var(--o-space-3)' }} htmlFor={`note-${item.id}`}>
              Internal note for {item.author?.name ?? 'this idea'}
            </label>
            <Input
              id={`note-${item.id}`}
              aria-label={`Internal note for ${item.author?.name ?? 'this idea'}`}
              value={notes[item.id] ?? item.managerNote ?? ''}
              onChange={(value: string) => setNotes((prev) => ({ ...prev, [item.id]: value }))}
            />
            <Text color="secondary">Only managers see this note.</Text>

            <div className="row-actions" style={{ marginTop: 'var(--o-space-3)', flexWrap: 'wrap' }}>
              <Button
                type="button"
                kind="primary"
                size="small"
                disabled={review.isPending}
                onClick={() =>
                  review.mutate({
                    id: item.id,
                    status: 'ADOPTED',
                    ...(notes[item.id] !== undefined ? { managerNote: notes[item.id]! } : {}),
                  })
                }
              >
                Adopt
              </Button>
              <Button
                type="button"
                kind="secondary"
                size="small"
                disabled={review.isPending}
                onClick={() =>
                  review.mutate({
                    id: item.id,
                    status: 'NOT_PURSUED',
                    ...(notes[item.id] !== undefined ? { managerNote: notes[item.id]! } : {}),
                  })
                }
              >
                Not pursued
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}
